import React, { useEffect, useState } from 'react';
import Loader from '../components/loader/loader';
import ShoppableVideo from '../components/shoppable-video/shoppable-video';
import TvShoppableVideo from '../components/silvr-tv/TvShoppableVideo';
import { ClipAccessError, deleteViewerPass, getPublishedClip, PublishedClip } from '../utils/clipApi';
import { useUserSettingsContext } from '../utils/context';
import type { Show } from '../shows';
import '../styles/app.scss';
import './show.scss';

/** A show's client page: its header, its published clip; a paused frame becomes shoppable with what
 *  the admins published. Rendered at "/" on the show's production hostname and at "/<key>" on the board. */
const ShowPage: React.FC<{ show: Show }> = ({ show }) => {
  const slug = show.slugs[0];
  const [clip, setClip] = useState<PublishedClip | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { setUserSettings } = useUserSettingsContext();

  useEffect(() => {
    let cancelled = false;
    setClip(null);
    setError(null);
    getPublishedClip(slug)
      .then((data) => {
        if (!cancelled) setClip(show.curate ? show.curate(data) : data);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        // No pass, an expired one, or an address blocked since it signed in (SIL-1595): back to the gate,
        // which asks again, rather than a "not available" message over a clip that is. The pass is handed
        // back first, so the gate shows its form even when GET /viewer-pass would still call the pass good
        // (another API replica that has not seen the block yet): otherwise it lets the browser straight
        // through, the clip refuses again, and the page spins between the two.
        if (err instanceof ClipAccessError) {
          void deleteViewerPass().then(() => {
            if (!cancelled) setUserSettings((prev) => ({ ...prev, isLoggedIn: false }));
          });
          return;
        }
        setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, show, setUserSettings]);

  // The 'tv' player carries the show's title over the picture itself, so the page header steps aside.
  const tv = show.player === 'tv';

  return (
    <div className="show-page">
      <div className="show-page__stage">
        {!tv && (
          <header className="show-header">
            <img src={show.logo} alt={show.logoAlt} className="show-header__logo" />
            <div className="show-header__text">
              <span className="show-header__title">{show.title}</span>
              <span className="show-header__subtitle">{show.subtitle} · pause to shop the scene</span>
            </div>
          </header>
        )}
        {clip ? (
          tv ? (
            <TvShoppableVideo clip={clip} clickSource={`demo-${show.key}`} title={show.title} episode={show.episode ?? show.subtitle} />
          ) : (
            <ShoppableVideo clip={clip} clickSource={`demo-${show.key}`} />
          )
        ) : error ? (
          <div className="clip-state" role="alert">
            <p>This clip is not available yet.</p>
            <p className="clip-state__detail">{error}</p>
          </div>
        ) : (
          <div className="clip-state" aria-live="polite">
            <Loader />
          </div>
        )}
      </div>
    </div>
  );
};

export default ShowPage;
